<template>
    <TransitionRoot
        as="template"
        :show="state.open"
    >
        <Dialog
            as="div"
            class="relative z-6000"
            :open="state.open === true"
            @close="dialogClose(state.overlayPreventClose)"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div
                    class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                        enter-to="opacity-100 translate-y-0 scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                    >
                        <DialogPanel
                            :class="[
                                state.maxWidthMax === true > 0
                                    ? `sm:w-full sm:max-w-5xl`
                                    : 'sm:w-full sm:max-w-xl',
                                'relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:p-6',
                            ]"
                        >
                            <div class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                <button
                                    type="button"
                                    class="cursor-pointer rounded-md bg-white text-gray-400 hover:text-gray-500"
                                    @click="dialogClose()"
                                >
                                    <span class="sr-only">Close</span>
                                    <MdiClose
                                        class="h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            <div class="select-none sm:flex sm:items-start">
                                <div
                                    class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-200 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <MdiApplicationBrackets
                                        class="h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <DialogTitle
                                        as="h3"
                                        class="text-base leading-6 font-semibold text-gray-900"
                                    >
                                        {{ state.title }}
                                    </DialogTitle>
                                    <div class="mt-2">
                                        <p class="text-sm text-gray-500">
                                            {{ state.text }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div
                                v-if="
                                    state.open &&
                                    activity.active === true &&
                                    activity.validated === false
                                "
                                class="max-w-modal-full max-h-[80vh] min-h-64 w-full grow overflow-y-auto select-none"
                            >
                                <Spinner
                                    type="dots"
                                    :color="`text-blue-600`"
                                    :color2="`text-blue-400`"
                                    :text-color="`text-gray-200`"
                                    :overlay="true"
                                    :speed="1000"
                                    :dots="true"
                                    :dot-char="'.'"
                                    :dot-dual="false"
                                    :dot-reverse="false"
                                    :text="t('loading')"
                                    :inline="false"
                                    :pulse="true"
                                />
                            </div>
                            <div
                                v-if="
                                    state.open &&
                                    activity.active === false &&
                                    activity.validated === true
                                "
                                class="max-w-modal-full max-h-[80vh] min-h-96 w-full grow overflow-y-auto p-4 text-sm text-gray-600"
                            >
                                <div class="mb-4 grid grid-cols-1 gap-2 lg:grid-cols-4 lg:gap-4">
                                    <div
                                        v-if="activity.data.compliant"
                                        class="flex-inline flex items-center justify-center gap-2 rounded-md bg-gray-200 p-2 font-bold text-green-500"
                                    >
                                        <MdiCheck
                                            class="inline h-4 w-4"
                                            aria-hidden="true"
                                        />
                                        {{ t('validationCompliant') }}
                                    </div>
                                    <div
                                        v-if="!activity.data.compliant"
                                        class="flex-inline flex items-center justify-center gap-2 rounded-md bg-gray-200 p-2 font-bold text-red-500"
                                    >
                                        <MdiAlert
                                            class="inline h-4 w-4"
                                            aria-hidden="true"
                                        />
                                        {{ t('validationNonCompliant') }}
                                    </div>
                                    <div
                                        class="flex-inline flex items-center justify-center gap-2 rounded-md bg-gray-200 p-2 font-bold"
                                    >
                                        {{ t('validationPassedChecks') }}
                                        {{ activity.data.passedChecks || '-' }}
                                    </div>
                                    <div
                                        class="flex-inline flex items-center justify-center gap-2 rounded-md bg-gray-200 p-2 font-bold"
                                    >
                                        {{ t('validationFailedChecks') }}

                                        {{ activity.data.failedChecks || '-' }}
                                    </div>
                                    <div
                                        class="flex-inline flex items-center justify-center gap-2 rounded-md bg-gray-200 p-2 font-bold"
                                    >
                                        {{ t('validationTotalChecks') }}

                                        {{ activity.data.totalChecks || '-' }}
                                    </div>
                                </div>
                                <AlertBox
                                    v-if="!activity.data.compliant && activity.data.errorMessage"
                                    :icon="MdiAlert"
                                    :title="t('Error')"
                                    state="danger"
                                >
                                    <div class="text-wrap break-all whitespace-pre-wrap">
                                        {{ activity.data.errorMessage || '-' }}
                                    </div>
                                </AlertBox>
                                <div
                                    v-if="!activity.data.compliant"
                                    class="flex flex-col items-start justify-start gap-2 py-4"
                                >
                                    <template
                                        v-for="(value, key) in activity.data.findings"
                                        :key="key"
                                    >
                                        <div
                                            class="flex w-full flex-col gap-2 rounded-md bg-gray-100 p-2 lg:grid lg:grid-cols-12 lg:gap-4"
                                        >
                                            <div
                                                class="flex flex-col items-start justify-start text-sm font-medium text-gray-900 lg:col-span-3"
                                            >
                                                <div class="p-1">
                                                    <strong
                                                        >{{ t('validationResultClause') }}:</strong
                                                    >
                                                    {{ value.clause }}
                                                </div>
                                                <div class="p-1">
                                                    <strong
                                                        >{{ t('validationResultRuleId') }}:</strong
                                                    >
                                                    {{ value.ruleId }}
                                                </div>
                                                <div class="p-1">
                                                    <strong
                                                        >{{
                                                            t('validationResultTestNumber')
                                                        }}:</strong
                                                    >
                                                    {{ value.testNumber }}
                                                </div>
                                            </div>
                                            <div
                                                class="flex flex-col items-start justify-start gap-y-2 text-sm font-medium text-gray-900 lg:col-span-9"
                                            >
                                                <div class="flex-none lg:flex lg:gap-2">
                                                    <strong class="w-20 flex-none">
                                                        {{ t('validationResultDescription') }}:
                                                    </strong>
                                                    <p>{{ value.description }}</p>
                                                </div>
                                                <div class="flex-none lg:flex lg:gap-2">
                                                    <strong class="w-20 flex-none">
                                                        {{ t('validationResultContext') }}:
                                                    </strong>
                                                    <pre
                                                        class="grow whitespace-pre-wrap text-gray-800"
                                                        >{{ value.context }}</pre
                                                    >
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>

                            <div class="mt-5 flex justify-end gap-2 select-none">
                                <ActionButton
                                    :value="t('buttonValidate')"
                                    state="success"
                                    :icon="MdiTestTube"
                                    @click="validate()"
                                />
                                <ActionButton
                                    :value="t('buttonClose')"
                                    state="neutral"
                                    :icon="MdiChevronLeft"
                                    @click="dialogClose()"
                                />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { reactive } from 'vue';
import ActionButton from '../ui/ActionButton.vue';
import { t } from '../composables/i18n';
import { validateTemplateVersion } from '../composables/useValidation';
import Spinner from '../ui/Spinner.vue';
import AlertBox from '../ui/AlertBox.vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import {
    MdiApplicationBrackets,
    MdiClose,
    MdiChevronLeft,
    MdiTestTube,
    MdiCheck,
    MdiAlert,
} from 'materialdesignicons-vue3/icons/';

defineExpose({
    name: 'TemplateValidationModal',
});

const props = defineProps({
    templateVersionId: {
        type: String,
        default: () => {
            return '';
        },
    },
    state: {
        type: Object,
        default: () => {
            return {
                open: false,
                title: '',
                text: '',
                maxWidthMax: false,
                data: {},
                overlayPreventClose: false,
            };
        },
    },
});

const state = props.state;
const templateVersionId = props.templateVersionId;
const activity = reactive({
    active: false,
    validated: false,
    data: {},
});

const validate = async () => {
    activity.active = true;
    activity.validated = false;
    activity.data = await validateTemplateVersion(templateVersionId);
    activity.active = false;
    activity.validated = true;
    // console.log('Validation result', activity.data);
};

const dialogClose = (preventClose = false) => {
    if (preventClose === false) {
        state.open = false;
    }
};
</script>
